import { Icon } from '../../utils/icons';

const AppointmentSettings = ({ config, onConfigChange }) => {
    const settings = config.settings || {};
    const language = settings.language || 'en-US';

    const languageOptions = [
        { code: 'en-US', name: 'English (United States)' },
        { code: 'en-GB', name: 'English (United Kingdom)' },
        { code: 'es-ES', name: 'Spanish (Spain)' },
        { code: 'es-MX', name: 'Spanish (Mexico)' },
        { code: 'fr-FR', name: 'French (France)' },
        { code: 'de-DE', name: 'German (Germany)' },
        { code: 'it-IT', name: 'Italian (Italy)' },
        { code: 'pt-BR', name: 'Portuguese (Brazil)' },
        { code: 'pt-PT', name: 'Portuguese (Portugal)' },
        { code: 'ru-RU', name: 'Russian (Russia)' },
        { code: 'zh-CN', name: 'Chinese (Simplified)' },
        { code: 'zh-TW', name: 'Chinese (Traditional)' },
        { code: 'ja-JP', name: 'Japanese (Japan)' },
        { code: 'ko-KR', name: 'Korean (Korea)' },
        { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
        { code: 'hi-IN', name: 'Hindi (India)' },
        { code: 'th-TH', name: 'Thai (Thailand)' },
        { code: 'vi-VN', name: 'Vietnamese (Vietnam)' },
        { code: 'tr-TR', name: 'Turkish (Turkey)' },
        { code: 'pl-PL', name: 'Polish (Poland)' },
        { code: 'nl-NL', name: 'Dutch (Netherlands)' },
        { code: 'sv-SE', name: 'Swedish (Sweden)' },
        { code: 'da-DK', name: 'Danish (Denmark)' },
        { code: 'no-NO', name: 'Norwegian (Norway)' },
        { code: 'fi-FI', name: 'Finnish (Finland)' }
    ];

    const dateDisplayMode = config.dateDisplayMode || 'horizontal';

    const handleLanguageChange = (lang) => {
        onConfigChange({
            settings: {
                ...settings,
                language: lang
            }
        });
    };

    const handleDateDisplayModeChange = (mode) => {
        onConfigChange({ dateDisplayMode: mode });
    };

    const allowMultiplePerSlot = settings.allowMultiplePerSlot === true;

    const handleAllowMultipleChange = (value) => {
        onConfigChange({
            settings: {
                ...settings,
                allowMultiplePerSlot: value
            }
        });
    };

    const dateViewOptions = [
        {
            id: 'horizontal',
            title: 'Horizontal View',
            description: 'Shows the next 7 days as a compact row. Date, time and details appear together.',
            icon: 'list'
        },
        {
            id: 'calendar',
            title: 'Calendar View',
            description: 'A full month calendar with a guided 3-step flow: pick a date, then a time, then enter details.',
            icon: 'schedule'
        }
    ];

    return (
        <div className="p-4 h-full overflow-y-auto">
            <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold mb-2">Settings</h3>
                <p className="text-sm text-gray-400 mb-8">
                    Configure the language and date selection view for your appointment widget
                </p>

                {/* Date Selection View */}
                <div className="mb-10">
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Date Selection View</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {dateViewOptions.map((option) => {
                            const isSelected = dateDisplayMode === option.id;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleDateDisplayModeChange(option.id)}
                                    className={`text-left p-4 rounded-lg border transition-all ${isSelected
                                        ? 'border-blue-500 bg-blue-500/10'
                                        : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                            <Icon name={option.icon} size={18} className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
                                            <span className={`text-sm font-semibold ${isSelected ? 'text-blue-300' : 'text-gray-200'}`}>
                                                {option.title}
                                            </span>
                                        </div>
                                        {isSelected && (
                                            <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                                                <Icon name="check" size={12} className="text-white" />
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 leading-relaxed">{option.description}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Booking Availability */}
                <div className="mb-10">
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Booking Availability</h4>
                    <div className="flex items-start justify-between p-4 rounded-lg border border-gray-700 bg-gray-800">
                        <div className="pr-4">
                            <div className="flex items-center space-x-2 mb-1">
                                <Icon name="schedule" size={18} className="text-gray-400" />
                                <span className="text-sm font-semibold text-gray-200">Allow multiple appointments per time slot</span>
                            </div>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                When enabled, more than one customer can book the same date and time. When
                                disabled, a slot is locked once someone books it.
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={allowMultiplePerSlot}
                            onClick={() => handleAllowMultipleChange(!allowMultiplePerSlot)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${allowMultiplePerSlot ? 'bg-blue-500' : 'bg-gray-600'}`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowMultiplePerSlot ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                        </button>
                    </div>
                </div>

                {/* Language Settings */}
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Language</h4>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                                Widget Language
                            </label>
                            <select
                                value={language}
                                onChange={(e) => handleLanguageChange(e.target.value)}
                                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm transition-colors"
                            >
                                {languageOptions.map((lang) => (
                                    <option key={lang.code} value={lang.code}>
                                        {lang.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-2">
                                Choose the language for your appointment booking widget
                            </p>
                        </div>

                        {/* Language Info */}
                        <div className="bg-gray-800 rounded-lg p-5 border border-gray-700">
                            <div className="flex items-start space-x-3">
                                <Icon name="info" size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h5 className="text-sm font-medium text-blue-300 mb-2">Language Support</h5>
                                    <p className="text-sm text-gray-400 leading-relaxed">
                                        The widget will display in the selected language. Date and time formats will automatically adjust to match the chosen locale for the best user experience.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentSettings;
